import React, { useEffect, useState } from 'react';
import {
    MapContainer,
    TileLayer,
    Polygon,
    Marker,
    useMap,
    useMapEvents
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FullscreenControl } from 'react-leaflet-fullscreen';

// Fix for default marker icon issue in Webpack
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png',
});

const FlyToLocation = ({ selectedProvince, selectedCity }) => {
    const map = useMap();

    useEffect(() => {
        if (selectedProvince && selectedProvince.centerLatitude && selectedProvince.centerLongitude) {
            map.flyTo([selectedProvince.centerLatitude, selectedProvince.centerLongitude], 8);
        }
    }, [selectedProvince, map]);

    useEffect(() => {
        if (selectedCity && selectedCity.centerLatitude && selectedCity.centerLongitude) {
            map.flyTo([selectedCity.centerLatitude, selectedCity.centerLongitude], 12);
        }
    }, [selectedCity, map]);

    return null;
};

const MapClickHandler = ({ onMapClick }) => {
    useMapEvents({
        click(e) {
            onMapClick(e.latlng);
        },
    });
    return null;
};

const DraggableMarker = ({ position, onMove, bounds }) => {
    const [markerPosition, setMarkerPosition] = useState(position);

    useEffect(() => {
        setMarkerPosition(position); // Sync marker position with prop changes
    }, [position]);

    const eventHandlers = {
        dragend(e) {
            const newPosition = e.target.getLatLng();
            setMarkerPosition(newPosition);
            const inside = bounds && bounds.length > 0 ? L.latLngBounds(bounds).contains(newPosition) : true;
            onMove(inside ? newPosition : null);
        },
    };

    return (
        <Marker
            position={markerPosition}
            draggable={true}
            eventHandlers={eventHandlers}
            icon={new L.Icon.Default()} // Use default icon to avoid conflicts
        />
    );
};

const ZoomToPolygon = ({ bounds }) => {
    const map = useMap();
    useEffect(() => {
        if (bounds?.length > 0) {
            map.fitBounds(bounds);
        }
    }, [bounds, map]);
    return null;
};

const LocationFinder = () => {
    const map = useMap();

    useEffect(() => {
        map.locate().on('locationfound', (e) => {
            map.flyTo(e.latlng, map.getZoom());
        });
    }, [map]);

    return null;
};

const CreateFileMap = ({ polygon, selectedProvince, selectedCity, onMarkerChange }) => {
    // Initialize markerPosition with a default value (center of Iran)
    const [markerPosition, setMarkerPosition] = useState({ lat: 32, lng: 53 });
    const [error, setError] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const fullscreen = () => {
        console.log("let it happen");

        setIsFullScreen(!isFullScreen)
    };
    useEffect(() => {
        if (polygon && polygon.length > 0) {
            const center = L.latLngBounds(polygon).getCenter();
            setMarkerPosition(center);
            onMarkerChange?.({ lat: center.lat, lng: center.lng }); // Notify parent of initial position
        }
    }, [polygon, onMarkerChange]);

    const handleMarkerMove = (newPosition) => {
        if (!newPosition) {
            setError(true);
            onMarkerChange?.(null);
            return;
        }

        const isInside = polygon && polygon.length > 0
            ? L.latLngBounds(polygon).contains(newPosition)
            : true;

        setMarkerPosition(newPosition);
        setError(!isInside);

        onMarkerChange?.(isInside ? {
            lat: newPosition.lat,
            lng: newPosition.lng,
        } : null);
    };

    return (
        <>
            {!isFullScreen && (<div className='h-[200px] w-full mb-3 relative'>
                <MapContainer
                    center={[32, 53]} // Center of Iran
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        attribution='© <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>'
                        url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
                    />

                    {/* Polygon display and zoom */}
                    {polygon && polygon.length > 0 && (
                        <>
                            <Polygon positions={polygon} pathOptions={{ color: 'green' }} />
                            <ZoomToPolygon bounds={polygon} />
                        </>
                    )}

                    {/* Handle map clicks */}
                    <MapClickHandler onMapClick={handleMarkerMove} />

                    {/* Draggable marker */}
                    {markerPosition && (
                        <DraggableMarker
                            position={markerPosition}
                            bounds={polygon}
                            onMove={handleMarkerMove}
                        />
                    )}

                    {/* Extra functionality */}
                    <LocationFinder />
                    <FullscreenControl position="topright" title="Fullscreen" />
                    <FlyToLocation selectedProvince={selectedProvince} selectedCity={selectedCity} />
                    <div onClick={fullscreen} className="shadow-md z-[70]">
                        <img src="/icons/zoomOut.svg" alt=" Location" class="absolute z-[1000] top-[10px] right-2 cursor-pointer" />
                    </div>
                </MapContainer>

                {error && (
                    <div className="absolute top-2 left-2 text-red-600 bg-white px-2 py-1 rounded shadow z-[1000]">
                        موقعیت انتخاب شده خارج از محدوده است!
                    </div>
                )}
            </div>)
            }
            {isFullScreen && (<div className='h-screen w-full mb-3 fixed top-0 right-0 '>
                <MapContainer
                    center={[32, 53]} // Center of Iran
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        attribution='© <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>'
                        url="http://{s}.tile.osm.org/{z}/{x}/{y}.png"
                    />

                    {/* Polygon display and zoom */}
                    {polygon && polygon.length > 0 && (
                        <>
                            <Polygon positions={polygon} pathOptions={{ color: 'green' }} />
                            <ZoomToPolygon bounds={polygon} />
                        </>
                    )}

                    {/* Handle map clicks */}
                    <MapClickHandler onMapClick={handleMarkerMove} />

                    {/* Draggable marker */}
                    {markerPosition && (
                        <DraggableMarker
                            position={markerPosition}
                            bounds={polygon}
                            onMove={handleMarkerMove}
                        />
                    )}

                    {/* Extra functionality */}
                    <LocationFinder />
                    <FullscreenControl position="topright" title="Fullscreen" />
                    <FlyToLocation selectedProvince={selectedProvince} selectedCity={selectedCity} />
                    <div onClick={fullscreen} className="shadow-md z-[70]">
                        <img src="/icons/zoomOut.svg" alt=" Location" class="absolute z-[1000] top-[90px] right-2 cursor-pointer" />
                    </div>
                </MapContainer>

                {error && (
                    <div className="absolute top-2 left-2 text-red-600 bg-white px-2 py-1 rounded shadow z-[1000]">
                        موقعیت انتخاب شده خارج از محدوده است!
                    </div>
                )}
            </div>)
            }

        </>


    );
};

export default CreateFileMap;